import { useMemo, type KeyboardEvent, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Chip,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import type {
  ApplicationDeviceRiskResponseDto,
  ApplicationJourneyHeaderDto,
  ApplicationVerificationSnapshotErrorDto,
} from '../../api/admin';
import { identityVerificationDashboardApi } from '../../api/identityVerificationDashboardApi';
import {
  buildApplicationOverviewAlerts,
  pickIdentityRowForOverviewAlerts,
  type OverviewServiceAlert,
} from '../../lib/applicationOverviewAlerts';
import type { ApplicationTabKey } from '../../lib/applicationDetailTabStatus';
import { applicationTabOpenLabel } from '../../lib/overviewServiceTab';
import { resolveOverviewPersonalDetails } from '../../lib/overviewPersonalDetails';
import { ApplicationSectionCard } from './applicationSectionChrome';
import { EditableValue } from './EditableDetailRow';

/** Quiet label | value row — compact labels so half-width cards still read cleanly. */
function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'minmax(112px, 132px) minmax(0, 1fr)' },
        columnGap: 1.5,
        rowGap: 0.35,
        alignItems: 'baseline',
      }}
    >
      <Typography
        component="div"
        sx={{
          fontSize: '0.8125rem',
          fontWeight: 600,
          color: 'text.secondary',
          lineHeight: 1.45,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          minWidth: 0,
          '& > .MuiTypography-root:first-of-type, & > .MuiChip-root': {
            fontWeight: 500,
            fontSize: '0.875rem',
            lineHeight: 1.45,
            color: 'text.primary',
          },
          '& > .MuiTypography-caption': {
            fontWeight: 400,
            display: 'block',
            mt: 0.2,
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function DetailList({ children }: { children: ReactNode }) {
  return (
    <Stack spacing={1.25} divider={<Divider flexItem />}>
      {children}
    </Stack>
  );
}

function FieldValue({ children, mono }: { children: ReactNode; mono?: boolean }) {
  return (
    <Typography
      variant="body2"
      sx={{
        fontWeight: 500,
        lineHeight: 1.45,
        ...(mono
          ? {
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
              fontSize: '0.8rem',
              wordBreak: 'break-all',
            }
          : null),
      }}
    >
      {children}
    </Typography>
  );
}

function formatMoney(n: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

function readIncomeItems(data: unknown): Record<string, unknown>[] {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return [];
  const raw = (data as { items?: unknown }).items;
  if (!Array.isArray(raw)) return [];
  return raw.filter((x): x is Record<string, unknown> => x != null && typeof x === 'object' && !Array.isArray(x));
}

function pickIncomeMetrics(row: Record<string, unknown> | undefined) {
  if (!row) return null;
  const metrics =
    row.metrics && typeof row.metrics === 'object' && !Array.isArray(row.metrics)
      ? (row.metrics as Record<string, unknown>)
      : {};
  const monthly =
    typeof metrics.normalizedMonthlyIncome === 'number' ? metrics.normalizedMonthlyIncome : null;
  const frequency =
    typeof metrics.frequencyClassification === 'string' && metrics.frequencyClassification.trim()
      ? metrics.frequencyClassification.trim()
      : null;
  const payor =
    typeof metrics.primaryPayor === 'string' && metrics.primaryPayor.trim()
      ? metrics.primaryPayor.trim()
      : null;
  const incomeClassRaw =
    (typeof metrics.incomeClass === 'string' && metrics.incomeClass.trim()
      ? metrics.incomeClass.trim()
      : null) ??
    (typeof row.incomeClass === 'string' && row.incomeClass.trim() ? row.incomeClass.trim() : null) ??
    (typeof row.finalIncomeClass === 'string' && row.finalIncomeClass.trim()
      ? row.finalIncomeClass.trim()
      : null);
  const incomeClass = incomeClassRaw ? incomeClassRaw.replace(/_/g, ' ') : null;
  const status = String(row.finalStatus ?? row.status ?? '').trim() || null;
  return { monthly, frequency, payor, incomeClass, status };
}

function OverviewAlertRow({
  alert,
  onNavigate,
}: {
  alert: OverviewServiceAlert;
  onNavigate?: (key: ApplicationTabKey) => void;
}) {
  const tab = alert.tabTarget;
  const interactive = Boolean(onNavigate && tab);
  const openLabel = tab ? applicationTabOpenLabel(tab) : null;

  const activate = () => {
    if (interactive && tab && onNavigate) onNavigate(tab);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (!interactive) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      activate();
    }
  };

  return (
    <Box
      role={interactive ? 'link' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={
        interactive && openLabel
          ? `${alert.service}: ${alert.title}. Open ${openLabel}`
          : undefined
      }
      onClick={interactive ? activate : undefined}
      onKeyDown={interactive ? onKeyDown : undefined}
      sx={(t) => ({
        borderRadius: 1.5,
        mx: -0.75,
        px: 0.75,
        py: 0.5,
        ...(interactive
          ? {
              cursor: 'pointer',
              transition: 'background-color 0.15s ease',
              '&:hover': {
                bgcolor: alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.06 : 0.12),
              },
              '&:focus-visible': {
                outline: `2px solid ${t.palette.primary.main}`,
                outlineOffset: 1,
              },
            }
          : null),
      })}
    >
      <Stack direction="row" spacing={0.75} alignItems="flex-start">
        <Stack spacing={0.35} sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={alert.service}
              color={
                alert.severity === 'error'
                  ? 'error'
                  : alert.severity === 'warning'
                    ? 'warning'
                    : 'default'
              }
              variant="outlined"
              sx={{ height: 22, fontWeight: 700 }}
            />
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {alert.title}
            </Typography>
          </Stack>
          {alert.detail ? (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45, pl: 0.25 }}>
              {alert.detail}
            </Typography>
          ) : null}
          {interactive && openLabel ? (
            <Typography
              variant="caption"
              color="primary"
              sx={{ fontWeight: 700, pl: 0.25, display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
            >
              Open {openLabel}
              <ChevronRightRoundedIcon sx={{ fontSize: 16 }} />
            </Typography>
          ) : null}
        </Stack>
        {interactive ? (
          <ChevronRightRoundedIcon
            sx={{ color: 'text.disabled', mt: 0.35, flexShrink: 0 }}
            fontSize="small"
          />
        ) : null}
      </Stack>
    </Box>
  );
}

export function ApplicationOverviewTab({
  application,
  employerFootprintStatus = null,
  employerFootprintAdminMessage = null,
  employerFootprintPaystubsReason = null,
  incomeRecords = null,
  employerRecords = null,
  deviceRisk = null,
  identitySessions = null,
  snapshotErrors = [],
  onNavigate,
}: {
  application: ApplicationJourneyHeaderDto;
  employerFootprintStatus?: string | null;
  employerFootprintAdminMessage?: string | null;
  employerFootprintPaystubsReason?: string | null;
  /** @deprecated Kept for call-site compatibility; alerts use footprint status only. */
  employerFootprintActive?: boolean;
  incomeRecords?: unknown | null;
  employerRecords?: unknown | null;
  deviceRisk?: ApplicationDeviceRiskResponseDto | null;
  /** Journey snapshot sessions — same source as the Identity tab status dot. */
  identitySessions?: unknown | null;
  snapshotErrors?: ApplicationVerificationSnapshotErrorDto[];
  /** Switch to the service tab that owns this Overview surface. */
  onNavigate?: (key: ApplicationTabKey) => void;
}) {
  const appId = application.applicationId.trim();
  const go = (key: ApplicationTabKey) => onNavigate?.(key);

  const lookupQ = useQuery({
    queryKey: ['applicationIdentityLookupByApp', appId],
    queryFn: () => identityVerificationDashboardApi.lookup({ applicationId: appId }),
    enabled: !!appId,
  });

  const identityItems = lookupQ.data?.items;
  const identityRow = useMemo(
    () => pickIdentityRowForOverviewAlerts(identityItems) ?? identityItems?.[0] ?? null,
    [identityItems],
  );
  const personal = useMemo(
    () => resolveOverviewPersonalDetails(application, identityRow),
    [application, identityRow],
  );

  const alerts = useMemo(
    () =>
      buildApplicationOverviewAlerts(application, {
        incomeRecords,
        employerRecords,
        deviceRisk,
        identityRows: identityItems,
        identityRow,
        identitySessions,
        snapshotErrors,
        employerFootprintStatus,
        employerFootprintAdminMessage,
        employerFootprintPaystubsReason,
      }),
    [
      application,
      deviceRisk,
      employerFootprintAdminMessage,
      employerFootprintPaystubsReason,
      employerFootprintStatus,
      employerRecords,
      identityItems,
      identityRow,
      identitySessions,
      incomeRecords,
      snapshotErrors,
    ],
  );

  const incomeSummary = useMemo(() => {
    const items = readIncomeItems(incomeRecords);
    return pickIncomeMetrics(items[0]);
  }, [incomeRecords]);

  const termMonths = application.loanSummary?.loanTermMonths;
  const showTerm = termMonths != null && termMonths > 0;
  const relatedPhone = application.phoneFromRelatedApplication;
  const identityPhone =
    (typeof identityRow?.customer?.phone_number === 'string' &&
    identityRow.customer.phone_number.trim()
      ? identityRow.customer.phone_number.trim()
      : null) ||
    (typeof identityRow?.phone_number === 'string' && identityRow.phone_number.trim()
      ? identityRow.phone_number.trim()
      : null);

  const overviewGridCols = { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' } as const;

  return (
    <Stack spacing={2.5}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: overviewGridCols,
          gap: 2,
          alignItems: 'stretch',
        }}
      >
        <ApplicationSectionCard
          title="Alerts raised by services"
          subtitle="Click an alert to open that service’s tab"
          icon={<NotificationsActiveRoundedIcon fontSize="small" />}
        >
          {alerts.length === 0 ? (
            <Alert severity="success" variant="outlined" sx={{ py: 0.75 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                No alerts raised by services
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.45 }}>
                Verification and decision services have not flagged this application.
              </Typography>
            </Alert>
          ) : (
            <Stack spacing={0.5} divider={<Divider flexItem />}>
              {alerts.map((alert) => (
                <OverviewAlertRow key={alert.id} alert={alert} onNavigate={onNavigate} />
              ))}
            </Stack>
          )}
        </ApplicationSectionCard>

        <ApplicationSectionCard
          title="Personal details"
          subtitle="Customer profile and ID capture"
          icon={<PersonRoundedIcon fontSize="small" />}
          onOpen={onNavigate ? () => go('identity') : undefined}
          openLabel={applicationTabOpenLabel('identity')}
        >
          <DetailList>
            {/* An aggregate of first and last name, so it carries no pencil: a
                control here would have to pick one of the two to edit. Both are
                editable as their own rows on the Identity tab. */}
            <DetailRow label="Full name">
              <FieldValue>{personal.fullName ?? '— not captured yet'}</FieldValue>
            </DetailRow>
            {/* Email and phone are OTP-verified and not editable — editing either
                would only replace a confirmed contact with an unconfirmed one. */}
            <DetailRow label="Email">
              <FieldValue>
                <Box component="span" sx={{ wordBreak: 'break-all' }}>
                  {application.email ?? '—'}
                </Box>
              </FieldValue>
              {application.attemptedEmail ? (
                <Typography variant="caption" color="text.secondary">
                  Last attempted: {application.attemptedEmail}
                </Typography>
              ) : null}
            </DetailRow>
            <DetailRow label="Phone">
                <Box>
              {application.phone ? (
                <>
                  <FieldValue>{application.phone}</FieldValue>
                  {application.phoneVerifiedAt ? (
                    <Typography variant="caption" color="success.main">
                      Verified {new Date(application.phoneVerifiedAt).toLocaleString()}
                    </Typography>
                  ) : null}
                </>
              ) : relatedPhone ? (
                <>
                  <FieldValue>{relatedPhone.phone}</FieldValue>
                  <Typography variant="caption" color="text.secondary">
                    From related application{' '}
                    <Link
                      component={RouterLink}
                      to={`/applications/${encodeURIComponent(relatedPhone.applicationId)}`}
                      underline="hover"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {relatedPhone.applicationId.slice(0, 8)}…
                    </Link>
                  </Typography>
                </>
              ) : identityPhone ? (
                <>
                  <FieldValue>{identityPhone}</FieldValue>
                  <Typography variant="caption" color="text.secondary">
                    From identity profile
                  </Typography>
                </>
              ) : (
                <FieldValue>—</FieldValue>
              )}
                </Box>
            </DetailRow>
            <DetailRow label="Date of birth">
              <EditableValue
                fieldKey="personal.dateOfBirth"
              >
              <FieldValue>
                {personal.dateOfBirth
                  ? (() => {
                      const t = Date.parse(personal.dateOfBirth);
                      return Number.isFinite(t)
                        ? new Date(t).toLocaleDateString()
                        : personal.dateOfBirth;
                    })()
                  : '—'}
              </FieldValue>
              </EditableValue>
            </DetailRow>
            <DetailRow label="Address">
              <EditableValue
                fieldKey="personal.address"
              >
                <FieldValue>
                  <Box component="span" sx={{ whiteSpace: 'pre-wrap' }}>
                    {personal.address ?? '—'}
                  </Box>
                </FieldValue>
              </EditableValue>
            </DetailRow>
            <DetailRow label="Customer ID">
              <FieldValue mono>{application.ccid ?? '— not linked yet'}</FieldValue>
            </DetailRow>
          </DetailList>
        </ApplicationSectionCard>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: overviewGridCols,
          gap: 2,
          alignItems: 'stretch',
        }}
      >
        <ApplicationSectionCard
          title="Loan details"
          subtitle="What they asked for — capacity from Decision engine"
          icon={<PaymentsRoundedIcon fontSize="small" />}
          onOpen={onNavigate ? () => go('decision') : undefined}
          openLabel={applicationTabOpenLabel('decision')}
        >
          <DetailList>
            <DetailRow label="Product">
              <Chip
                size="small"
                variant="outlined"
                label={application.productType.replace(/_/g, ' ')}
                sx={{ fontWeight: 500 }}
              />
            </DetailRow>
            <DetailRow label="Requested amount">
              <EditableValue
                fieldKey="loan.requestedAmount"
              >
                <FieldValue>
                  {application.loanSummary?.requestedPrincipal != null
                    ? `$${application.loanSummary.requestedPrincipal.toLocaleString()}`
                    : '—'}
                </FieldValue>
              </EditableValue>
            </DetailRow>
            {/* Derived from principal and the product's term bands, so it is not
                editable — an independently set term contradicts the band it came
                from and is recomputed away. */}
            <DetailRow label="Term">
              <FieldValue>
                {showTerm
                  ? `${termMonths} months`
                  : application.productType === 'PAYDAY_LOAN'
                    ? 'Payday (no fixed term)'
                    : '—'}
              </FieldValue>
            </DetailRow>
            <DetailRow label="APR">
              <FieldValue>
                {application.loanSummary?.interestRateApr != null
                  ? `${application.loanSummary.interestRateApr}%`
                  : '—'}
              </FieldValue>
            </DetailRow>
            {application.decisionEngine?.maxEligibleAmount != null ? (
              <DetailRow label="Max eligible">
                <FieldValue>
                  ${Number(application.decisionEngine.maxEligibleAmount).toLocaleString()}
                </FieldValue>
              </DetailRow>
            ) : null}
            <DetailRow label="Opened">
              <FieldValue>{new Date(application.createdAt).toLocaleString()}</FieldValue>
            </DetailRow>
          </DetailList>
        </ApplicationSectionCard>

        {incomeSummary ? (
          <ApplicationSectionCard
            title="Income analysis"
            subtitle="Latest bank income verification"
            icon={<AccountBalanceRoundedIcon fontSize="small" />}
            onOpen={onNavigate ? () => go('income') : undefined}
            openLabel={applicationTabOpenLabel('income')}
          >
            <DetailList>
              <DetailRow label="Avg monthly pay">
                <FieldValue>
                  {incomeSummary.monthly != null ? formatMoney(incomeSummary.monthly) : '—'}
                </FieldValue>
              </DetailRow>
              <DetailRow label="Pay frequency">
                <FieldValue>{incomeSummary.frequency ?? '—'}</FieldValue>
              </DetailRow>
              <DetailRow label="Payor / employer">
                <EditableValue
                  fieldKey="employment.bankPayrollName"
                >
                  <FieldValue>{incomeSummary.payor ?? '—'}</FieldValue>
                </EditableValue>
              </DetailRow>
              <DetailRow label="Income class">
                <FieldValue>{incomeSummary.incomeClass ?? '—'}</FieldValue>
              </DetailRow>
              <DetailRow label="Outcome">
                <FieldValue>
                  {incomeSummary.status ? incomeSummary.status.replace(/_/g, ' ') : '—'}
                </FieldValue>
              </DetailRow>
            </DetailList>
          </ApplicationSectionCard>
        ) : null}
      </Box>
    </Stack>
  );
}
